import type { AuthTokenResponse } from '@/features/auth/types'
import { AUTH_ENDPOINTS } from '@/features/auth/api/auth.endpoints'
import { bareHttp } from '@/core/api/http'
import {
  isHardRefreshFailure,
} from '@/core/auth/refresh-failure'
import {
  isRecruitmentPortalAppPath,
  isRecruitmentPortalLoginPath,
  isRecruitmentPortalPath,
} from '@/core/auth/auth-client-context'
import {
  PORTAL_AUTH_CLIENT,
  PORTAL_AUTH_REFRESH_LOCK_KEY,
  clearPortalAuthTokens,
  clearPortalLogoutPending,
  getPortalAccessQrToken,
  getPortalAccessToken,
  getPortalAccessExpiresAt,
  isPortalLogoutPending,
  isPortalRememberLogin,
  setPortalAuthTokens,
  shouldRefreshPortalAccessToken,
} from '@/core/auth/portal-storage'

let refreshPromise: Promise<void> | null = null
let keeperTimer: ReturnType<typeof setInterval> | null = null

const KEEPER_INTERVAL_MS = 30_000
const REFRESH_LOCK_TTL_MS = 20_000
const REFRESH_WAIT_TIMEOUT_MS = 25_000
const REFRESH_WAIT_POLL_MS = 150

export function redirectToPortalLogin(): void {
  clearPortalAuthTokens()
  stopPortalAuthSessionKeeper()
  window.dispatchEvent(new CustomEvent('auth:portal-session-cleared'))
  if (!isRecruitmentPortalLoginPath()) {
    const access = getPortalAccessQrToken()?.trim() || ''
    const params = new URLSearchParams()
    if (isRecruitmentPortalPath()) {
      params.set('redirect', window.location.pathname + window.location.search)
    }
    const query = params.toString()
    const base = access
      // Keep raw token in path (same as QR / backend) — do not encode; Zalo + Vue decode can desync.
      ? `/recruitment-portal/login/a/${access}`
      : '/recruitment-portal/login'
    window.location.replace(`${base}${query ? `?${query}` : ''}`)
  }
}

function readRefreshLock(): number | null {
  const raw = localStorage.getItem(PORTAL_AUTH_REFRESH_LOCK_KEY)
  if (!raw) return null
  const lockedAt = Number(raw)
  return Number.isFinite(lockedAt) ? lockedAt : null
}

function acquireRefreshLock(): boolean {
  const now = Date.now()
  const lockedAt = readRefreshLock()
  if (lockedAt !== null && now - lockedAt < REFRESH_LOCK_TTL_MS) {
    return false
  }
  localStorage.setItem(PORTAL_AUTH_REFRESH_LOCK_KEY, String(now))
  return true
}

function releaseRefreshLock(): void {
  localStorage.removeItem(PORTAL_AUTH_REFRESH_LOCK_KEY)
}

export function isPortalAuthRefreshLockHeld(): boolean {
  const lockedAt = readRefreshLock()
  return lockedAt !== null && Date.now() - lockedAt < REFRESH_LOCK_TTL_MS
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function waitForPeerRefresh(beforeExpiresAt: number | null): Promise<boolean> {
  const deadline = Date.now() + REFRESH_WAIT_TIMEOUT_MS
  while (Date.now() < deadline) {
    const lockedAt = readRefreshLock()
    if (lockedAt === null || Date.now() - lockedAt >= REFRESH_LOCK_TTL_MS) {
      break
    }

    const expiresAt = getPortalAccessExpiresAt()
    if (expiresAt !== null && expiresAt !== beforeExpiresAt && expiresAt > Date.now()) {
      return true
    }

    await wait(REFRESH_WAIT_POLL_MS)
  }

  return !shouldRefreshPortalAccessToken()
}

async function postPortalRefresh(): Promise<void> {
  if (isPortalLogoutPending()) {
    clearPortalAuthTokens()
    throw new Error('portal logout pending')
  }

  const accessQr = getPortalAccessQrToken()
  if (!accessQr) {
    throw new Error('portal access qr missing')
  }

  const { data } = await bareHttp.post<AuthTokenResponse>(
    AUTH_ENDPOINTS.REFRESH,
    { auth_client: PORTAL_AUTH_CLIENT, access: accessQr },
    {
      headers: {
        'X-Auth-Client': PORTAL_AUTH_CLIENT,
        'X-Portal-Access': accessQr,
      },
    },
  )

  if (!data?.access_token) {
    throw new Error('Portal refresh response missing access_token')
  }

  setPortalAuthTokens(
    {
      access_token: data.access_token,
      expires_in: data.expires_in,
    },
    isPortalRememberLogin(),
  )
  clearPortalLogoutPending()
}

export async function refreshPortalSessionForced(): Promise<void> {
  if (!isRecruitmentPortalAppPath()) {
    throw new Error('Refresh skipped outside portal app')
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const beforeExpiresAt = getPortalAccessExpiresAt()

      for (let attempt = 0; attempt < 3; attempt++) {
        if (!shouldRefreshPortalAccessToken()) {
          return
        }

        if (!acquireRefreshLock()) {
          const peerRefreshed = await waitForPeerRefresh(beforeExpiresAt)
          if (peerRefreshed || !shouldRefreshPortalAccessToken()) {
            return
          }
          continue
        }

        try {
          await postPortalRefresh()
          return
        } finally {
          releaseRefreshLock()
        }
      }

      if (shouldRefreshPortalAccessToken()) {
        throw new Error('Refresh lock busy')
      }
    })().finally(() => {
      refreshPromise = null
    })
  }

  await refreshPromise
}

export async function ensureValidPortalAccessToken(): Promise<void> {
  if (!getPortalAccessToken()) {
    throw new Error('no portal access token')
  }
  if (!shouldRefreshPortalAccessToken()) {
    return
  }
  await refreshPortalSessionForced()
}

export function startPortalAuthSessionKeeper(): void {
  if (keeperTimer !== null) return
  keeperTimer = setInterval(() => {
    if (!isRecruitmentPortalAppPath()) return
    if (!getPortalAccessToken() || !shouldRefreshPortalAccessToken()) return
    void refreshPortalSessionForced().catch((error) => {
      if (isHardRefreshFailure(error) || (error instanceof Error && error.message.includes('access qr'))) {
        redirectToPortalLogin()
      }
    })
  }, KEEPER_INTERVAL_MS)
}

export function stopPortalAuthSessionKeeper(): void {
  if (keeperTimer !== null) {
    clearInterval(keeperTimer)
    keeperTimer = null
  }
}
